import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session cookie (httpOnly) is the primary credential — send it on every request.
axios.defaults.withCredentials = true;

// Create axios instance with interceptors
const axiosInstance = axios.create({ withCredentials: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // In-memory only (never persisted) — Authorization header back-compat during the session.
  const [token, setToken] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  const logout = useCallback(() => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    setToken(null);
    setUser(null);
    setImpersonatedUser(null);
    setOriginalAdmin(null);
  }, []);

  // Set up axios interceptor to handle token expiration
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.detail || '';
          if (errorMessage.toLowerCase().includes('token expired') || 
              errorMessage.toLowerCase().includes('invalid token')) {
            logout();
            window.location.href = '/login?expired=true';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Restore session from the httpOnly cookie on first load
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchUser = async (attempt = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: 20000 });
      const userData = response.data;
      setUser(userData);
      
      // Check if this is an impersonation session
      if (userData.impersonated_by) {
        setImpersonatedUser(userData);
        setOriginalAdmin({
          id: userData.impersonated_by,
          name: userData.impersonated_by_name
        });
      } else {
        setImpersonatedUser(null);
        setOriginalAdmin(null);
      }
      setLoading(false);
    } catch (error) {
      // 401 with no cookie/session simply means "not logged in".
      // Transient backend failures (504/503/network blips) MUST NOT clear
      // the session — retry so the user stays logged in once the backend recovers.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
        setLoading(false);
      } else if (attempt < 3) {
        const delay = 1500 * Math.pow(2, attempt);
        setTimeout(() => fetchUser(attempt + 1), delay);
      } else {
        setLoading(false);
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, name, invite_token) => {
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      name,
      invite_token
    });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`, {}, getAuthHeader());
      const { token: newToken, user: targetUser, impersonated_by } = response.data;
      
      setToken(newToken);
      setUser(targetUser);
      setImpersonatedUser(targetUser);
      setOriginalAdmin(impersonated_by);
      
      return targetUser;
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await axios.post(`${API}/admin/stop-impersonation`, {}, getAuthHeader());
      const { token: newToken, user: adminUser } = response.data;
      
      setToken(newToken);
      setUser(adminUser);
      setImpersonatedUser(null);
      setOriginalAdmin(null);
      
      return adminUser;
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      throw error;
    }
  };

  // Cookie is the credential; header included only while an in-memory token exists.
  // Memoized so effects/callbacks depending on it don't re-run every render.
  const getAuthHeader = useCallback(() => (
    token ? { headers: { Authorization: `Bearer ${token}` } } : { headers: {} }
  ), [token]);

  // Use axiosInstance for API calls (has interceptor for token expiration)
  const apiCall = useCallback((config) => {
    return axiosInstance({
      ...config,
      headers: {
        ...config.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      signup, 
      logout, 
      getAuthHeader,
      apiCall,
      impersonateUser, 
      stopImpersonation,
      impersonatedUser,
      originalAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
